import React from 'react';
import { NpsCampaignResultsBuslineDto } from '@area-restrita/api-client/model/npsCampaignResultsBuslineDto';
import { NpsCampaignResultsScheduleDto } from '@area-restrita/api-client/model/npsCampaignResultsScheduleDto';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  formatApiNumber,
  formatApiPercent,
  formatInteger,
  formatScheduleDays,
  formatTime,
  shouldShowSegmentMetrics,
} from '@/modules/nps/utils';

type Segment = NpsCampaignResultsBuslineDto | NpsCampaignResultsScheduleDto;

interface IProps {
  segments: Segment[];
  kind: 'busline' | 'schedule';
  isLoading: boolean;
}

const getSegmentName = (segment: Segment, kind: IProps['kind']) => {
  if (kind === 'busline') {
    const busline = segment as NpsCampaignResultsBuslineDto;

    return `${busline.code} - ${busline.name}`;
  }

  const schedule = segment as NpsCampaignResultsScheduleDto;

  return `${schedule.buslineCode} - ${schedule.buslineName}`;
};

const getSchedulePeriod = (segment: Segment, kind: IProps['kind']) => {
  if (kind !== 'schedule') return null;

  const schedule = segment as NpsCampaignResultsScheduleDto;

  return `${formatTime(schedule.startAt)} - ${formatTime(schedule.endAt)}`;
};

const getSegmentId = (segment: Segment, kind: IProps['kind']) =>
  kind === 'busline'
    ? (segment as NpsCampaignResultsBuslineDto).id
    : (segment as NpsCampaignResultsScheduleDto).evaluatedScheduleVersionId;

const NpsResultsSegmentTable: React.FC<IProps> = ({
  segments,
  kind,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="rounded-md border p-4 text-sm text-muted-foreground">
        {kind === 'busline' ? 'Carregando linhas...' : 'Carregando horários...'}
      </div>
    );
  }

  if (!segments.length) {
    return (
      <div className="rounded-md border p-4 text-sm text-muted-foreground">
        {kind === 'busline'
          ? 'Nenhuma linha possui respostas nesta campanha.'
          : 'Nenhum horário possui respostas nesta campanha.'}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Alert>
        <AlertDescription>
          {kind === 'busline'
            ? 'Uma resposta pode estar associada a mais de uma linha. Por isso, os totais por linha podem ser maiores que o total de respostas da campanha.'
            : 'Uma resposta pode estar associada a mais de um horário. Por isso, os totais por horário podem ser maiores que o total de respostas da campanha.'}
        </AlertDescription>
      </Alert>

      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Linha</TableHead>
              {kind === 'schedule' && <TableHead>Horário</TableHead>}
              {kind === 'schedule' && <TableHead>Dias da semana</TableHead>}
              <TableHead className="text-right">Respostas</TableHead>
              <TableHead className="text-right">NPS</TableHead>
              <TableHead className="text-right">Nota média</TableHead>
              <TableHead>Distribuição</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {segments.map((segment) => {
              const showMetrics = shouldShowSegmentMetrics(segment);

              return (
                <TableRow key={`${kind}-${getSegmentId(segment, kind)}`}>
                  <TableCell className="font-medium">
                    {getSegmentName(segment, kind)}
                  </TableCell>
                  {kind === 'schedule' && (
                    <TableCell>{getSchedulePeriod(segment, kind)}</TableCell>
                  )}
                  {kind === 'schedule' && (
                    <TableCell>
                      {formatScheduleDays(
                        (segment as NpsCampaignResultsScheduleDto).daysOfWeek,
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    {formatInteger(segment.responseCount)}
                  </TableCell>
                  {showMetrics ? (
                    <>
                      <TableCell className="text-right">
                        {formatApiNumber(segment.nps)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatApiNumber(segment.averageScore)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="secondary">
                            Promotores{' '}
                            {formatApiPercent(
                              segment.distribution?.promoterPercentage,
                            )}
                          </Badge>
                          <Badge variant="secondary">
                            Neutros{' '}
                            {formatApiPercent(
                              segment.distribution?.neutralPercentage,
                            )}
                          </Badge>
                          <Badge variant="secondary">
                            Detratores{' '}
                            {formatApiPercent(
                              segment.distribution?.detractorPercentage,
                            )}
                          </Badge>
                        </div>
                      </TableCell>
                    </>
                  ) : (
                    <TableCell colSpan={3} className="text-muted-foreground">
                      Resultado não exibido para preservar a confidencialidade
                      dos respondentes.
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default NpsResultsSegmentTable;
